import React, { ReactNode, useCallback, useState } from 'react';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { AI_INTERVIEWER_DEBUG_MODE_SELECT_ITEMS } from 'libs/end-to-end/src/ai-interviewer';
import { AI_INTERVIEWER_DEBUG_MODE_DEFAULT } from 'libs/end-to-end/src/types/ai-interviewer/ai-interviewer-debug-mode';
import { AIInterviewerDebugMode } from 'libs/end-to-end/src/types/ai-interviewer/ai-interviewer-debug-mode';
import { CompositeDataSetAiInterviewerRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { useRubricConstructsData } from 'apps/sigma2-ui/src/data-wizard/components/use-rubric-constructs-data';
import { AllowedOpenAiModels } from 'libs/end-to-end/src/types/databases/data-sets/data-set-resume-rubric';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { SelectiveFilterStringBasedOptionComponent } from 'apps/sigma2-ui/src/SelectiveFilteringOptionComponent';
import { Modal } from 'apps/sigma2-ui/src/components/ui/Modal';
import { DatabaseRubricBankConstructsView } from 'apps/sigma2-ui/src/data-wizard/components/DatabaseRubricBankConstructsView';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';

export const DataSetCompositeRecipeAIInterviewerRubricConfigComponent = ({
  organizationId,
  databaseId,
  config,
  setConfig,
}: {
  organizationId: string;
  databaseId: string;
  config: CompositeDataSetAiInterviewerRecipe;
  setConfig: (config: CompositeDataSetRecipe) => void;
}) => {
  const setFilteringConfig = useCallback(
    (configs: CompositeDataSetAiInterviewerRecipe['rubricsByJobTitle']) => {
      setConfig({
        ...config,
        rubricsByJobTitle: configs,
      });
    },
    [config, setConfig],
  );

  const filteringConfigOps = useCrudOperations({
    items: config.rubricsByJobTitle ?? [],
    setItems: setFilteringConfig,
  });

  const useRubricConstructsDataPayload = useRubricConstructsData({
    organizationId,
    databaseId,
    isSelectEnabled: true,
  });

  const [showConstructsModal, setShowConstructsModal] = useState(false);
  const [currentRubricConfigIndex, setCurrentRubricConfigIndex] = useState<
    number | null
  >(null);

  return (
    <Spacing size={'md'} direction={'vertical'}>
      <Modal
        show={showConstructsModal}
        onClose={() => {
          setShowConstructsModal(false);
          setCurrentRubricConfigIndex(null);
        }}
        title={'Choose Rubric Items'}
        maxWidthOverride="max-w-screen-md"
        scrollable={true}
      >
        <DatabaseRubricBankConstructsView
          payload={useRubricConstructsDataPayload}
          isEditable={false}
        />

        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              if (
                currentRubricConfigIndex !== null &&
                useRubricConstructsDataPayload.singleSelectConfig
                  ?.currentConstructId
              ) {
                filteringConfigOps.onEdit(currentRubricConfigIndex, {
                  rubricConstructId:
                    useRubricConstructsDataPayload.singleSelectConfig
                      .currentConstructId,
                });
              }

              setShowConstructsModal(false);
              setCurrentRubricConfigIndex(null);
            },
          }}
          variant={'primary'}
          size={'small'}
          text={'Save'}
        />
      </Modal>

      <SelectBoxWithSearch
        type="single"
        list={AI_INTERVIEWER_DEBUG_MODE_SELECT_ITEMS}
        label={'AI Interviewer Debug Mode'}
        selected={
          config.aiInterviewerDebugMode ?? AI_INTERVIEWER_DEBUG_MODE_DEFAULT
        }
        setSelected={(value: string) => {
          if (value !== null) {
            setConfig({
              ...config,
              aiInterviewerDebugMode: AIInterviewerDebugMode.parse(value),
            });
          }
        }}
      />

      <Spacing size={'lg'} direction={'vertical'}>
        <SelectBoxWithSearch
          type={'single'}
          setSelected={(value) => {
            setConfig({
              ...config,
              openAiModel: value as AllowedOpenAiModels,
            });
          }}
          selected={config.openAiModel}
          list={AllowedOpenAiModels.options.map((model) => {
            return {
              optionLabel: model,
              optionValue: model,
            };
          })}
          label={'OpenAI Model'}
        />

        <div>
          <S2Checkbox
            checked={config.enableHallucinationFilter ?? false}
            onChange={() => {
              setConfig({
                ...config,
                enableHallucinationFilter: !config.enableHallucinationFilter,
              });
            }}
          >
            Enable Hallucination Filter
          </S2Checkbox>
          <div className="text-sm text-gray-600 ml-6 mt-1">
            Automatically removes common transcription hallucinations like
            &quot;Thanks for watching&quot; and &quot;Please subscribe&quot;
            from video interview responses
          </div>
        </div>

        <div>
          <S2Checkbox
            checked={config.combineAnswersInPipeline ?? false}
            onChange={() => {
              setConfig({
                ...config,
                combineAnswersInPipeline: !config.combineAnswersInPipeline,
              });
            }}
          >
            Combine Answers in Pipeline
          </S2Checkbox>
          <div className="text-sm text-gray-600 ml-6 mt-1">
            If there is concern of the order in answers being inconsistent, set
            this to true so that the answers are combined in the pipeline and
            grade all provided answers together.
          </div>
        </div>

        <div>
          <S2Checkbox
            checked={config.runOnDagster ?? false}
            onChange={() => {
              setConfig({
                ...config,
                runOnDagster: !config.runOnDagster,
              });
            }}
          >
            Run on Dagster
          </S2Checkbox>
          <div className="text-sm text-gray-600 ml-6 mt-1">
            When enabled, Dagster writes the input files directly, skipping the
            dependent dataset python script step.
          </div>
        </div>

        <div>
          <S2Checkbox
            checked={config.runBadLanguageDetector ?? false}
            onChange={() => {
              setConfig({
                ...config,
                runBadLanguageDetector: !config.runBadLanguageDetector,
              });
            }}
          >
            Run bad language detector
          </S2Checkbox>
          <div className="text-sm text-gray-600 ml-6 mt-1">
            After AI interviewer scoring, classify distinct candidate answers
            for hate speech and related language; results are cached per
            organization and written to a join file for applicants with complete
            coverage.
          </div>
        </div>

        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              filteringConfigOps.onAdd({
                filter: {
                  type: 'equals',
                  value: '',
                },
                rubricConstructId: '',
              });
            },
          }}
          size={'small'}
          variant={'primary'}
          text={'Create'}
        />

        <Table
          items={config.rubricsByJobTitle ?? []}
          columnDefinitions={[
            {
              id: 'filter',
              header: () => 'Filter',
              cell: ({ item, rowIndex }) => (
                <SelectiveFilterStringBasedOptionComponent
                  option={item.filter}
                  setOption={(option) => {
                    filteringConfigOps.onEdit(rowIndex, {
                      filter: option,
                    });
                  }}
                />
              ),
            },
            {
              id: 'rubric_item',
              header: () => 'Rubric Item',
              cell: ({ item, rowIndex }) => {
                const foundItem =
                  useRubricConstructsDataPayload.constructs.find(
                    (i) => i.id === item.rubricConstructId,
                  );

                let itemIdItem: ReactNode;

                if (item.rubricConstructId === '' || !foundItem) {
                  itemIdItem = <div>{'N/A'}</div>;
                } else {
                  itemIdItem = <div>{foundItem.name}</div>;
                }

                return (
                  <Spacing size={'md'} direction={'vertical'}>
                    {itemIdItem}
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: () => {
                          useRubricConstructsDataPayload.singleSelectConfig?.setCurrentConstructId(
                            item.rubricConstructId,
                          );
                          setCurrentRubricConfigIndex(rowIndex);
                          setShowConstructsModal(true);
                        },
                      }}
                      size={'small'}
                      variant={'outline'}
                      text={'Select Item'}
                    />
                  </Spacing>
                );
              },
            },
            {
              id: 'delete',
              header: () => 'Delete',
              cell: ({ rowIndex }) => (
                <Button
                  behavior={{
                    type: 'onClick',
                    onClick: () => {
                      filteringConfigOps.onDelete(rowIndex);
                    },
                  }}
                  size={'small'}
                  variant={'destructive'}
                  text={'Delete'}
                />
              ),
            },
          ]}
        />
      </Spacing>
    </Spacing>
  );
};
